/**
 * The three mutations behind the built-in automation templates — lead
 * rotation, CRM webhook ingestion, and date reminders.
 *
 * Extracted from `BuiltInAutomationLibrary.tsx`, which only needs to call
 * `.mutate()` / read `.isPending` on the results; the request plumbing and
 * success/error handling live here.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from 'sonner';

import { REMINDER_AUTOMATION_QUERY_KEY } from '@/hooks/useReminderAutomationEnabled';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { apiPost } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import type { CrmWebhookSecretData } from './CrmWebhookSecretModal';

export interface UseBuiltInAutomationMutationsArgs {
  orgId: number | undefined;
  /** Called with the webhook secret once the CRM ingestion mutation succeeds. */
  onCrmWebhookSecret: (data: CrmWebhookSecretData) => void;
}

export function useBuiltInAutomationMutations({
  orgId,
  onCrmWebhookSecret,
}: UseBuiltInAutomationMutationsArgs) {
  const queryClient = useQueryClient();

  // Invalidate by entity type rather than by a caller-supplied query key, so
  // both hosts refresh without either having to hand its key over.
  const invalidateAutomations = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: crmQueryKeys.objects(orgId ?? 0, 'ahoy_event_automation'),
      exact: false,
    });
  }, [queryClient, orgId]);

  const createFromEndpoint = useCallback(
    <T>(script: string): Promise<T> => {
      if (!orgId) {
        return Promise.reject(new Error('Organization not available'));
      }
      return apiPost<T>(`/api/orgs/${orgId}/crm/automations/${script}/`, {});
    },
    [orgId]
  );

  const leadRotationMutation = useMutation({
    mutationFn: () => createFromEndpoint<unknown>('lead_rotation'),
    onSuccess: () => {
      invalidateAutomations();
      toast.success('Contact rotation automation created');
    },
    onError: err => {
      logger.error(
        'Failed to create automation',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't create the automation");
    },
  });

  const crmWebhookMutation = useMutation({
    mutationFn: () => createFromEndpoint<CrmWebhookSecretData>('crm_ingestion'),
    onSuccess: data => {
      onCrmWebhookSecret(data);
      invalidateAutomations();
    },
    onError: err => {
      logger.error(
        'Failed to set up CRM ingestion webhook',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't set up the CRM ingestion webhook");
    },
  });

  const reminderMutation = useMutation({
    mutationFn: () =>
      createFromEndpoint<{
        automation_entity_id: string;
        backfill_count: number | null;
        task_id: string | null;
        backfill_error?: string;
      }>('reminder_from_date_properties'),
    onSuccess: data => {
      invalidateAutomations();
      queryClient.invalidateQueries({
        queryKey: crmQueryKeys.objects(orgId ?? 0, 'ahoy_reminder'),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['crm', orgId, REMINDER_AUTOMATION_QUERY_KEY],
      });
      if (data && typeof data === 'object' && 'backfill_error' in data) {
        toast.warning(
          'Date reminders enabled, but backfill could not run – reminders will catch up automatically.'
        );
      } else {
        toast.success('Date reminders enabled');
      }
    },
    onError: err => {
      // 409 means the automation already exists. Opt-in is idempotent at the
      // resource level, so this is a success from the user's point of view —
      // reporting it as a failure is just wrong, and the second click on an
      // already-added template is the common way to reach it.
      const statusCode =
        err !== null &&
        typeof err === 'object' &&
        'statusCode' in err &&
        typeof (err as Record<string, unknown>).statusCode === 'number'
          ? (err as { statusCode: number }).statusCode
          : undefined;
      if (statusCode === 409) {
        logger.info(
          'Date reminder opt-in already processed (409); invalidating reminder queries',
          { component: 'crm_events', org_id: orgId }
        );
        invalidateAutomations();
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(orgId ?? 0, 'ahoy_reminder'),
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: ['crm', orgId, REMINDER_AUTOMATION_QUERY_KEY],
        });
        toast.success('Date reminders already enabled');
        return;
      }
      logger.error(
        'Failed to enable date reminders',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error("We couldn't enable date reminders");
    },
  });

  return { leadRotationMutation, crmWebhookMutation, reminderMutation };
}
